import { useEffect, useState } from "react";
import { useSession } from "@/auth/session";
import { createMobileDevice, deleteMobileDevice, listMobileDevices, type MobileDeviceItem, updateMobileDevice } from "@/lib/api";

type FormState = { deviceCode: string; name: string; status: "active" | "inactive" };
const emptyForm: FormState = { deviceCode: "", name: "", status: "active" };

export default function DispositivosTab() {
  const { session } = useSession();
  const [items, setItems] = useState<MobileDeviceItem[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError(null);
      try {
        const rows = await listMobileDevices(session);
        if (!cancelled) setItems(rows);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudieron cargar dispositivos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }
  function openCreateModal(): void {
    resetForm();
    setShowCreateModal(true);
  }
  function closeCreateModal(): void {
    setShowCreateModal(false);
    resetForm();
  }
  function editItem(item: MobileDeviceItem): void {
    setEditingId(item.id);
    setForm({ deviceCode: item.deviceCode, name: item.name, status: item.status === "inactive" ? "inactive" : "active" });
  }

  async function onSave(): Promise<void> {
    if (!session || !form.deviceCode.trim() || !form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = { deviceCode: form.deviceCode.trim().toUpperCase(), name: form.name.trim(), status: form.status };
      if (editingId) {
        const updated = await updateMobileDevice(session, editingId, payload);
        setItems((prev) => prev.map((item) => (item.id === editingId ? updated : item)));
        resetForm();
      } else {
        const created = await createMobileDevice(session, payload);
        setItems((prev) => [created, ...prev]);
        closeCreateModal();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar dispositivo");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id: string): Promise<void> {
    if (!session || !window.confirm("¿Eliminar dispositivo?")) return;
    try {
      await deleteMobileDevice(session, id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      if (editingId === id) resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo eliminar dispositivo");
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">Dispositivos ({items.length})</h2>
          <button onClick={openCreateModal} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <i className="ri-add-line" />
            Nuevo dispositivo
          </button>
        </div>

        {loading ? <div className="h-16 bg-gray-100 rounded animate-pulse" /> : (
          <div className="space-y-2">
            {items.length === 0 ? <p className="text-sm text-gray-500">Sin dispositivos registrados.</p> : items.map((item) => (
              <div key={item.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                <div>
                  <div className="text-sm font-medium text-gray-800">{item.name}</div>
                  <div className="text-xs text-gray-500 font-mono">{item.deviceCode}</div>
                  <div className="text-xs text-gray-400">Última sync: {item.lastSyncAt || "-"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${item.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>{item.status}</span>
                  <button onClick={() => editItem(item)} className="text-xs px-2 py-1 border border-gray-200 rounded">Editar</button>
                  <button onClick={() => void onDelete(item.id)} className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded">Eliminar</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {editingId ? (
          <div className="mt-4 border border-gray-100 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Editar dispositivo</h3>
            <div className="grid grid-cols-4 gap-3">
              <input value={form.deviceCode} onChange={(e) => setForm((prev) => ({ ...prev, deviceCode: e.target.value.toUpperCase() }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value as "active" | "inactive" }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
              <button onClick={() => void onSave()} disabled={saving || !form.deviceCode.trim() || !form.name.trim()} className="px-3 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-60">{saving ? "Guardando..." : "Actualizar"}</button>
            </div>
            <button onClick={resetForm} className="mt-3 text-xs px-3 py-1.5 border border-gray-200 rounded-lg">Cancelar edición</button>
          </div>
        ) : null}
        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-gray-100 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Nuevo dispositivo</h3>
              <button onClick={closeCreateModal} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400"><i className="ri-close-line" /></button>
            </div>
            <div className="space-y-3">
              <input value={form.deviceCode} onChange={(e) => setForm((prev) => ({ ...prev, deviceCode: e.target.value.toUpperCase() }))} placeholder="Código (ej: DEVICE-01)" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="Nombre dispositivo" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
              <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value as "active" | "inactive" }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={closeCreateModal} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg">Cancelar</button>
              <button onClick={() => void onSave()} disabled={saving || !form.deviceCode.trim() || !form.name.trim()} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-60">{saving ? "Creando..." : "Crear dispositivo"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
